import React from 'react';
import { CONSTANTS } from '@tpco/tpco-saturn-types';
import { ThomasCampaign, ProductFulfillment, ProductFulfillmentVersion } from 'models/interfaces';
import memoize from 'memoize-one';
import { TableRow } from 'models/interfaces/Table';
import Table from '../Table/TableComponent';
import {
  campaignHeadings,
  createCampaignRow,
  p3CampaignHeadings
} from './ThomasCampaignListConfig';
import { toast } from 'react-toastify';
import { ApolloClient } from 'apollo-client';
import {
  DUPLICATE_CAMPAIGN_MUTATION,
  DELETE_CAMPAIGN_MUTATION
} from 'containers/ThomasCampaignContainer';
import { GET_ADVERTISER_DATA_QUERY_PRODUCT } from 'containers/ThomasAdvertiserCampaignsContainer';
import { confirmDeletion, confirmDuplication } from 'util/confirmation';
import { canBeOptimized, canBeDeleted } from 'util/p3Validation';

interface ThomasCampaignListProps {
  tgramsId: string;
  campaigns: ThomasCampaign[];
  productFulfillmentsVersions: ProductFulfillmentVersion[];
  productFulfillments: ProductFulfillment[];
  client: ApolloClient<any>;
  optimizeCampaign: (campain: any) => void;
  productId: number;
  isUserAuth: boolean;
}

const ThomasCampaignList: React.FC<ThomasCampaignListProps> = ({
  campaigns,
  productFulfillmentsVersions,
  productFulfillments,
  tgramsId,
  client,
  optimizeCampaign,
  productId,
  isUserAuth
}: ThomasCampaignListProps) => {
  // create handlers

  const duplicateCampaign = async (campaignId: string) => {
    const confirm = confirmDuplication('campaign');
    if (confirm) {
      await client.mutate({
        mutation: DUPLICATE_CAMPAIGN_MUTATION,
        variables: { campaign_id: campaignId },
        refetchQueries: [
          {
            query: GET_ADVERTISER_DATA_QUERY_PRODUCT,
            variables: {
              tgrams_id: tgramsId,
              product_id: productId
            }
          }
        ],
        awaitRefetchQueries: true
      });
      toast.success('Campaign successfully duplicated');
    } else {
      return null;
    }
  };

  const deleteCampaign = async (campaignId: string) => {
    const confirm = confirmDeletion('campaign');
    if (confirm) {
      await client.mutate({
        mutation: DELETE_CAMPAIGN_MUTATION,
        variables: { campaign_id: campaignId },
        refetchQueries: [
          {
            query: GET_ADVERTISER_DATA_QUERY_PRODUCT,
            variables: {
              tgrams_id: tgramsId,
              product_id: productId
            }
          }
        ],
        awaitRefetchQueries: true
      });
      toast.success('Campaign successfully deleted');
    } else {
      return null;
    }
  };

  // build campaignsMap id => campaign and trs in single loop
  const buildRenderDataFromProps = (
    tc: ThomasCampaign[],
    tgramsId: string,
    pfv: ProductFulfillmentVersion[],
    fulfilments: ProductFulfillment[]
  ) => {
    return tc.reduce<TableRow[]>((rows, x) => {
      // shallow copy campaign to save from disappearing due to no-cache
      const copy: ThomasCampaign = { ...x };
      let opportunityId = '';
      let productSku = copy.product_id == 1 ? 'PVS' : 'PPP';
      let quantity = '';
      if (pfv && pfv.length > 0) {
        pfv.find((x: ProductFulfillmentVersion) => {
          if (x.campaign_id === copy.campaign_id) {
            opportunityId = x.opportunity_id;
          }
        });
      }
      if (opportunityId != '' && fulfilments && fulfilments.length > 0) {
        fulfilments.find((x: ProductFulfillment) => {
          if (opportunityId === x.opportunity_id && x.product_sku === productSku) {
            quantity = x.quantity.toString();
          }
        });
      }

      const campaignIsEditable = true; //!no_more_changes_campaign.includes(copy.status_id);
      const isP3 = copy.product_id === CONSTANTS.CAMPAIGN_PRODUCT_TYPE.POINTS; // P3
      const campaignIsOptimizable =
        isP3 && canBeOptimized(copy.status_id, copy.start_date, copy.end_date, opportunityId); //CAMPAIGN_STATUS.PUBLISHED;
      const campaignIsDeletable =
        isUserAuth && canBeDeleted(copy.status_id, opportunityId, copy.product_id);
      const campaignIsDuplicable = isUserAuth;
      //console.log(`campaign status`, copy)
      // create campaign tr and add to array
      const tr = createCampaignRow(
        copy,
        tgramsId,
        campaignIsEditable,
        campaignIsOptimizable,
        campaignIsDeletable,
        campaignIsDuplicable,
        duplicateCampaign,
        deleteCampaign,
        optimizeCampaign,
        isP3,
        productId,
        opportunityId,
        quantity
      );
      rows.push(tr);

      return rows;
    }, new Array<TableRow>());
  };

  // memoize render data
  const memoizedRenderData = memoize(buildRenderDataFromProps);
  const campaignRows = memoizedRenderData(
    campaigns,
    tgramsId,
    productFulfillmentsVersions,
    productFulfillments
  );
  const headings =
    productId && productId == CONSTANTS.CAMPAIGN_PRODUCT_TYPE.POINTS
      ? p3CampaignHeadings
      : campaignHeadings;
  return <Table headings={headings} data={campaignRows} />;
};

export default ThomasCampaignList;
